import { Undef } from 'types/UtilityTypes';

/**
 * Turvo LTL Service type options for the Service type dropdown.
 * To map to Turvo's {id, key, value} objects
 */
export const TURVO_SERVICE_TYPE_OPTIONS = [
  { label: 'Any', value: 'any' },
  { label: 'Expedite', value: 'expedite' },
  { label: 'Overnight AM', value: 'overnight-am' },
  { label: 'Overnight', value: 'overnight' },
  { label: 'Overnight PM', value: 'overnight-pm' },
  { label: 'Second Day AM', value: 'second-day-am' },
  { label: 'Second Day', value: 'second-day' },
  { label: 'Second Day PM', value: 'second-day-pm' },
  { label: 'Third Day AM', value: 'third-day-am' },
  { label: 'Third Day', value: 'third-day' },
  { label: 'International Express', value: 'international-express' },
  { label: 'International', value: 'international' },
  { label: 'Economy', value: 'economy' },
  { label: 'Guaranteed End of Day', value: 'guaranteed-end-of-day' },
  { label: 'Guaranteed Morning', value: 'guaranteed-morning' },
  { label: 'Guaranteed Noon', value: 'guaranteed-noon' },
  { label: 'Premium', value: 'premium' },
  { label: 'Standard', value: 'standard' },
] as const;

export const TURVO_SERVICE_TYPE_DEFAULT = 'any' as const;

/**
 * Sentinel value for "no payment term" - Radix Select does not allow value="",
 * so we use this and map to '' when building the API request.
 */
export const TURVO_PAYMENT_TERM_NONE = '__none__' as const;

/**
 * Turvo LTL Payment terms options for the Payment terms dropdown.
 * Values are the stable keys expected by the BE mapping layer.
 * Uses TURVO_PAYMENT_TERM_NONE for "None" since Radix Select rejects value="".
 */
export const TURVO_PAYMENT_TERM_OPTIONS = [
  { label: 'None', value: TURVO_PAYMENT_TERM_NONE },
  { label: 'Prepaid', value: 'prepaid' },
  { label: 'Collect', value: 'collect' },
  { label: 'Third Party', value: 'third-party' },
] as const;

/** Default when no payment term is selected (maps to empty for Turvo API). */
export const TURVO_PAYMENT_TERM_DEFAULT = TURVO_PAYMENT_TERM_NONE;

/**
 * Turvo LTL pickup service options.
 * Values are strict text values from Turvo's pickup services mapping list.
 */
const TURVO_PICKUP_SERVICE_VALUES = [
  'Driver count',
  'Driver load',
  'Inside pickup',
  'Liftgate',
  'Residential',
  'Hook trailer',
  'Unhook trailer',
  'After hours',
  'Detention',
  'Detention with power',
  'Detention without power',
  'Drayage',
  'Driver assistance',
  'Driver layover',
  'Drop trailer',
  'Escort',
  'Holiday',
  'Layover',
  'Limited access',
  'Loading',
  'Lumper',
  'Notify before arrival',
  'Permit',
  'Pickup appointment',
  'Reconsignment',
  'Storage',
  'Tradeshow',
  'TWIC services',
  'Weekend',
  'Dangerous Goods',
  'Hazmat',
  'Over Dimensions',
  'COD',
  'Guaranteed',
  'Guaranteed AM',
  'Urgent',
  'Stackable',
  'Bulkhead',
  'Expedited',
  'Certificate of Origin',
  'Express',
  'Deferred',
  'Standard',
  'Signature Required',
  'HAL',
  'Team Driver',
  'Backhaul',
  'WHS',
  'GOI',
  'NMC',
  'Saturday Pickup',
  'Holiday Pickup',
  'Sort and segregate',
  'Pallet jack',
  'Pickup Marking and Tagging',
  'NYC Metro',
  'Airport Pickup',
  'Camp Pickup',
  'Container Freight Station Pickup',
  'Church Pickup',
  'Country Club Pickup',
  'Convention/Tradeshow Pickup',
  'Construction Site Pickup',
  'Dock Pickup',
  'School Pickup',
  'Farm Pickup',
  'Government Site Pickup',
  'Grocery Warehouse Pickup',
  'Hospital Pickup',
  'Hotel Pickup',
  'Military Installation Pickup',
  'Mine Site Pickup',
  'Native American Reservation Pickup',
  'Non-Business Hours Pickup',
  'Nursing Home Pickup',
  'Fair/Amusement/Park Pickup',
  'Pier Pickup',
  'Prison Pickup',
  'Self-Storage Pickup',
  'Utility Site Pickup',
  'Mini-Storage Pickup',
  'Farm Country Club Estate Pickup',
  'Driver secure',
  'Exclusive use trailer',
  'Tanker Endorsement',
  'Coil Racks',
  'Edge Protectors',
  'Chain Protectors',
  'Ramps',
  'AirRide',
  'PPE safety gear',
  'Straps',
  'GPS tracking',
  'Heavy lifting',
  'Load levelers',
  'Pipe stakes & dunnage',
  'Transloading',
  'Winch',
  'Upper deck ramps',
  'Bumper pull',
  'Driver load/unload',
  'Temporary tags',
  'Trailer Hitch Connection Type Pintle',
  'Trailer Hitch Connection Type Ball',
  'Brakes Type Air',
  'Brakes Type Electric',
  'Enclosed Trailer',
  'Tarps',
  'Pipe Stakes',
  'Dunnage',
  'Driver Takedown',
  'Tow Takedown',
  'Driver Setup/Install',
  'Tow Setup/Install',
  'Loading and Local Pickup',
  'Unloading',
  'Unloading and Local Delivery',
  'Trip Charge',
  'On-Site relocation',
  'Block and Brace',
  'Dock height',
  'Pre-pull',
  'Chassis',
  'Chassis Split',
  'Chassis Flip',
  'Tri-axle',
  'Dry-run',
  'Refrigerated',
  'Debris Removal',
  'Furniture Dolly',
  'Trailer Washout',
  'Threshold',
  'White glove',
  'CARB',
] as const;

const TURVO_DELIVERY_SERVICE_VALUES = [
  'AirRide',
  'Backhaul',
  'Block and Brace',
  'Brakes Type: Air',
  'Brakes Type: Electric',
  'Bulkhead',
  'Bumper pull',
  'CARB',
  'Certificate of Origin',
  'Chain Protectors',
  'Chassis',
  'Chassis Split',
  'Chassis Flip',
  'Coil Racks',
  'COD',
  'Deferred',
  'Debris Removal',
  'Delivery Marking and Tagging',
  'Detention',
  'Detention without power',
  'Distribution Center Delivery',
  'Dock Delivery',
  'Dock height',
  'Driver assistance',
  'Driver layover',
  'Drayage',
  'Driver load/unload',
  'Driver count',
  'Driver secure',
  'Driver Takedown',
  'Detention with power',
  'Driver unload',
  'Driver Setup/Install',
  'Dry-run',
  'Drop trailer',
  'Dunnage',
  'Edge Protectors',
  'Delivery Appointment',
  'Dangerous Goods',
  'Mall Delivery',
  'Lumper',
  'NMC',
  'Loading and Local Pickup',
  'Notify before arrival',
  'Non-Business Hours Delivery',
  'On-Site relocation',
  'Non-Commercial Delivery',
  'Mini-Storage Delivery',
  'Expedited',
  'Fair/Amusement/Park Delivery',
  'Express',
  'Farm Country Club Estate Delivery',
  'GOI',
  'Furniture Dolly',
  'Guaranteed',
  'Exclusive use trailer',
  'Guaranteed AM',
  'Hazmat',
  'GPS tracking',
  'Heavy lifting',
  'Holiday',
  'Holiday Delivery',
  'HAL',
  'Inside delivery',
  'Layover',
  'Escort',
  'Limited access',
  'Limited access - Camp Delivery',
  'Limited access - Church Delivery',
  'Limited access - Construction Site Delivery',
  'Limited access - Airport Delivery',
  'Limited access - Container Freight Station Delivery',
  'Limited access - Country Club Delivery',
  'Limited access - Convention/Tradeshow Delivery',
  'Limited access - Government Site Delivery',
  'Limited access - Farm Delivery',
  'Liftgate',
  'Limited access - Hospital Delivery',
  'Limited access - Grocery Warehouse Delivery',
  'Limited access - Hotel Delivery',
  'Limited access - Native American Reservation Delivery',
  'Limited access - Military Installation Delivery',
  'Hook trailer',
  'Limited access - Storage',
  'Limited access - School Delivery',
  'Limited access - Self-Storage Delivery',
  'Limited access - Tradeshow',
  'Limited access - Prison Delivery',
  'Limited access - Mine Site Delivery',
  'Over Dimensions',
  'NYC Metro',
  'Load levelers',
  'Limited access - Utility Site Delivery',
  'Limited access - Nursing Home Delivery',
  'Enclosed Trailer',
  'White glove',
  'Pipe stakes & dunnage',
  'Pipe Stakes',
  'Pre-pull',
  'PPE safety gear',
  'Ramps',
  'Reconsignment',
  'Winch',
  'WHS',
  'Weekend',
  'Urgent',
  'Upper deck ramps',
  'Unloading and Local Delivery',
  'Trailer Washout',
  'Transloading',
  'Tri-axle',
  'Trip Charge',
  'TWIC services',
  'Unhook trailer',
  'Unloading',
  'Trailer Hitch Connection Type: Ball',
  'Tow Takedown',
  'Tow Setup/Install',
  'Threshold',
  'Temporary tags',
  'Standard',
  'Sunday Delivery',
  'Tanker Endorsement',
  'Tarps',
  'Team Driver',
  'Trailer Hitch Connection Type: Pintle',
  'Stackable',
  'Sort and segregate',
  'Signature Required',
  'Saturday Delivery',
  'Pier Delivery',
  'Redelivery',
  'Refrigerated',
  'Permit',
  'Residential',
  'Pallet jack',
  'Resort Delivery',
  'Straps',
] as const;

export const TURVO_PICKUP_SERVICE_OPTIONS = TURVO_PICKUP_SERVICE_VALUES.map(
  (value) => ({ value, label: value })
);

export const TURVO_PICKUP_SERVICE_DEFAULT: string[] = [];
export const TURVO_DELIVERY_SERVICE_OPTIONS = TURVO_DELIVERY_SERVICE_VALUES.map(
  (value) => ({ value, label: value })
);
export const TURVO_DELIVERY_SERVICE_DEFAULT: string[] = [];

/**
 * Turvo LTL Qty unit options (handling unit type).
 */
export const TURVO_QTY_UNIT_OPTIONS = [
  { value: 'bags', label: 'Bags' },
  { value: 'bales', label: 'Bales' },
  { value: 'barrels', label: 'Barrels' },
  { value: 'base units', label: 'Base units' },
  { value: 'bin', label: 'Bin' },
  { value: 'blocks', label: 'Blocks' },
  { value: 'bottles', label: 'Bottles' },
  { value: 'boxes', label: 'Boxes' },
  { value: 'buckets', label: 'Buckets' },
  { value: 'bulk', label: 'Bulk' },
  { value: 'bundles', label: 'Bundles' },
  { value: 'bushels', label: 'Bushels' },
  { value: 'cans', label: 'Cans' },
  { value: 'carboys', label: 'Carboys' },
  { value: 'carpets', label: 'Carpets' },
  { value: 'cartons', label: 'Cartons' },
  { value: 'cases', label: 'Cases' },
  { value: 'coils', label: 'Coils' },
  { value: 'combinations', label: 'Combinations' },
  { value: 'containers', label: 'Containers' },
  { value: 'crates', label: 'Crates' },
  { value: 'cylinders', label: 'Cylinders' },
  { value: 'drums', label: 'Drums' },
  { value: 'each', label: 'Each' },
  { value: 'feet', label: 'Feet' },
  { value: 'gallons', label: 'Gallons' },
  { value: 'grams', label: 'Grams' },
  { value: 'hundredweight on net', label: 'Hundredweight on Net' },
  { value: 'inner pack', label: 'Inner Pack' },
  { value: 'items', label: 'Items' },
  { value: 'kegs', label: 'Kegs' },
  { value: 'kgs', label: 'Kgs' },
  { value: 'kit', label: 'Kit' },
  { value: 'layer', label: 'Layer' },
  { value: 'lbs', label: 'Lbs' },
  { value: 'liters', label: 'Liters' },
  { value: 'loose', label: 'Loose' },
  { value: 'metric tons', label: 'Metric tons' },
  { value: 'other', label: 'Other' },
  { value: 'pack', label: 'Pack' },
  { value: 'packages', label: 'Packages' },
  { value: 'pails', label: 'Pails' },
  { value: 'pair', label: 'Pair' },
  { value: 'pallets', label: 'Pallets' },
  { value: 'piece', label: 'Piece' },
  { value: 'pouches', label: 'Pouches' },
  { value: 'racks', label: 'Racks' },
  { value: 'ream', label: 'Ream' },
  { value: 'reels', label: 'Reels' },
  { value: 'rolls', label: 'Rolls' },
  { value: 'skids', label: 'Skids' },
  { value: 'spack', label: 'Spack' },
  { value: 'spool', label: 'Spool' },
  { value: 'tons', label: 'Tons' },
  { value: 'totes', label: 'Totes' },
  { value: 'trays', label: 'Trays' },
  { value: 'truckload', label: 'Truckload' },
  { value: 'tubes/pipes', label: 'Tubes/pipes' },
  { value: 'tubs', label: 'Tubs' },
  { value: 'units', label: 'Units' },
  { value: 'vehicles', label: 'Vehicles' },
] as const;

export type TurvoQtyUnitKey = (typeof TURVO_QTY_UNIT_OPTIONS)[number]['value'];

export const TURVO_QTY_UNIT_DEFAULT =
  'pallets' as const satisfies TurvoQtyUnitKey;

export const TURVO_QTY_UNIT_LABEL_BY_KEY: Record<TurvoQtyUnitKey, string> =
  Object.fromEntries(
    TURVO_QTY_UNIT_OPTIONS.map((o) => [o.value, o.label])
  ) as Record<TurvoQtyUnitKey, string>;

export function getTurvoHandlingUnitTypeName(unitKey: Undef<string>): string {
  if (!unitKey || unitKey.trim() === '') {
    return TURVO_QTY_UNIT_LABEL_BY_KEY[TURVO_QTY_UNIT_DEFAULT];
  }

  const key = unitKey.trim() as TurvoQtyUnitKey;
  return key in TURVO_QTY_UNIT_LABEL_BY_KEY
    ? TURVO_QTY_UNIT_LABEL_BY_KEY[key]
    : key;
}

/**
 * Turvo qty unit lookup keys (from Turvo metadata).
 * Key values are used by search response handlingUnitTypeId.
 */
const TURVO_QTY_UNIT_VALUE_TO_TYPE_KEY: Record<TurvoQtyUnitKey, string> = {
  bags: '6003',
  bales: '6004',
  barrels: '6035',
  'base units': '6039',
  bin: '6047',
  blocks: '6033',
  bottles: '6040',
  boxes: '6001',
  buckets: '6056',
  bulk: '6034',
  bundles: '6005',
  bushels: '6026',
  cans: '6006',
  carboys: '6007',
  carpets: '6008',
  cartons: '6009',
  cases: '6022',
  coils: '6010',
  combinations: '6057',
  containers: '6027',
  crates: '6011',
  cylinders: '6012',
  drums: '6013',
  each: '6037',
  feet: '6051',
  gallons: '6028',
  grams: '6053',
  'hundredweight on net': '6058',
  'inner pack': '6052',
  items: '6055',
  kegs: '6024',
  kgs: '6031',
  kit: '6048',
  layer: '6054',
  lbs: '6030',
  liters: '6032',
  loose: '6002',
  'metric tons': '6025',
  other: '6021',
  pack: '6049',
  packages: '6041',
  pails: '6014',
  pair: '6050',
  pallets: '6038',
  piece: '6043',
  pouches: '6059',
  racks: '6042',
  ream: '6044',
  reels: '6015',
  rolls: '6016',
  skids: '6017',
  spack: '6045',
  spool: '6046',
  tons: '6029',
  totes: '6018',
  trays: '6060',
  truckload: '6023',
  'tubes/pipes': '6019',
  tubs: '6061',
  units: '6036',
  vehicles: '6020',
};

export const TURVO_QTY_UNIT_TYPE_ID_TO_VALUE: Record<number, TurvoQtyUnitKey> =
  Object.fromEntries(
    Object.entries(TURVO_QTY_UNIT_VALUE_TO_TYPE_KEY).map(([value, key]) => [
      Number(key),
      value,
    ])
  ) as Record<number, TurvoQtyUnitKey>;

/**
 * Turvo LTL dimension units.
 */
export const TURVO_DIMENSION_UNIT_OPTIONS = [
  { value: 'in', label: 'in' },
  { value: 'ft', label: 'ft' },
  { value: 'cm', label: 'cm' },
  { value: 'm', label: 'm' },
  { value: 'mm', label: 'mm' },
  { value: 'yard', label: 'yard' },
] as const;

export type TurvoDimensionUnitKey =
  (typeof TURVO_DIMENSION_UNIT_OPTIONS)[number]['value'];

export const TURVO_DIMENSION_UNIT_LABEL_BY_KEY: Record<
  TurvoDimensionUnitKey,
  string
> = Object.fromEntries(
  TURVO_DIMENSION_UNIT_OPTIONS.map((o) => [o.value, o.label])
) as Record<TurvoDimensionUnitKey, string>;

/**
 * Turvo LTL weight units.
 */
export const TURVO_WEIGHT_UNIT_OPTIONS = [
  { value: 'lb', label: 'lb' },
  { value: 'kg', label: 'kg' },
  { value: 't', label: 't' },
  { value: 'oz', label: 'oz' },
  { value: 'ton', label: 'ton' },
  { value: 'g', label: 'g' },
  { value: 'lt', label: 'lt' },
] as const;

export type TurvoWeightUnitKey =
  (typeof TURVO_WEIGHT_UNIT_OPTIONS)[number]['value'];

export const TURVO_WEIGHT_UNIT_LABEL_BY_KEY: Record<
  TurvoWeightUnitKey,
  string
> = Object.fromEntries(
  TURVO_WEIGHT_UNIT_OPTIONS.map((o) => [o.value, o.label])
) as Record<TurvoWeightUnitKey, string>;

export const TURVO_WEIGHT_UNIT_TO_FORM_WEIGHT_UNIT: Record<string, string> = {
  lb: 'Pound',
  lbs: 'Pound',
  kg: 'Kilogram',
  t: 'Tonne',
  ton: 'Ton',
  oz: 'Ounce',
  g: 'Gram',
  lt: 'Long Ton',
};

export const TURVO_DIM_UNIT_TO_FORM_DIMENSION_UNIT: Record<string, string> = {
  in: 'Inch',
  cm: 'Centimeter',
  ft: 'Foot',
  m: 'Meter',
  mm: 'Millimeter',
  yard: 'Yard',
  yd: 'Yard',
};

/**
 * Turvo default values.
 */
export const TURVO_DEFAULT_QUOTE_PHASE = {
  key: '2000',
  value: 'Quote',
} as const;

export const TURVO_DEFAULT_TRANSPORTATION_MODE_LTL = {
  key: '24107',
  value: 'LTL',
} as const;

export const ITEM_CARD_ANIMATIONS = {
  card: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  },
  toggle: {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
    transition: { duration: 0.15 },
  },
  actions: {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
    transition: { duration: 0.1 },
  },
} as const;
